import { type FC, type KeyboardEvent } from "react";

import { StickAttributes } from "@ui/components/StickAttributes";
import { useTranslations } from "@ui/providers/useTranslations";
import { getOptimizedMagentoImageUrl } from "@ui/utils/getOptimizedMagentoImageUrl";
import classnames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { PackageCrossIcon } from "@jti/brand-icons/package-cross";
import { StoreDefaultIcon } from "@jti/brand-icons/store-default";
import { Loader } from "@jti/core-components/components/Loader";
import type { MagentoFlavorAttribute, MagentoStockStatus } from "@jti/magento-types";

import styles from "./SingleFlavorBlock.module.scss";

export interface SingleFlavorBlockProps {
  label: string;
  description: string;
  imageUrl: string;
  stockStatus: MagentoStockStatus;
  isActive?: boolean;
  isStockStatusLoading: boolean;
  attributes?: MagentoFlavorAttribute[];
  onClick: () => void;
}

const KEYBOARD_ACTIVATION_KEYS = new Set([" ", "Enter"]);

// Matches the fixed height of the .image element in SingleFlavorBlock.module.scss (height: 89px).
const FLAVOR_IMAGE_HEIGHT_PX = 89;
const FLAVOR_IMAGE_DENSITIES = [1, 2, 3] as const;

const getOptimizedFlavorImageUrl = (imageUrl: string, density: number) =>
  getOptimizedMagentoImageUrl({ url: imageUrl, height: FLAVOR_IMAGE_HEIGHT_PX * density });

export const SingleFlavorBlock: FC<SingleFlavorBlockProps> = ({
  label,
  description,
  imageUrl,
  stockStatus,
  isActive = false,
  isStockStatusLoading = false,
  attributes,
  onClick,
}) => {
  const isInStock = stockStatus === "IN_STOCK";
  const isAvailableInStores = stockStatus === "AVAILABLE_IN_STORES";
  const { showFlavourAttributesInFlavourSelector = false, showFlavourDescriptionInFlavourSelector = false } =
    useConfig();
  const { __ } = useTranslations();

  const optimizedImageUrls = imageUrl
    ? FLAVOR_IMAGE_DENSITIES.map((density) => getOptimizedFlavorImageUrl(imageUrl, density))
    : [];
  const optimizedImageSrcSet = imageUrl
    ? optimizedImageUrls
        .map((optimizedImageUrl, index) => `${optimizedImageUrl} ${FLAVOR_IMAGE_DENSITIES[index]}x`)
        .join(", ")
    : undefined;

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (KEYBOARD_ACTIVATION_KEYS.has(event.key)) {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={classnames(styles.block, {
        [styles.inStock]: isInStock,
        [styles.outOfStock]: !isInStock,
        [styles.active]: isActive,
      })}
      data-testid="single-flavor-stick-block"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-disabled={!isInStock}
    >
      <header className={styles.header}>
        <strong className={styles.label}>{label}</strong>
        <img
          className={styles.image}
          data-testid="single-flavor-block-image"
          src={optimizedImageUrls[0]}
          srcSet={optimizedImageSrcSet}
          alt=""
          loading="lazy"
          decoding="async"
        />

        {showFlavourDescriptionInFlavourSelector && description && (
          <div className={styles.subheader} data-testid="flavourDescription">
            {description}
          </div>
        )}
      </header>

      {showFlavourAttributesInFlavourSelector && attributes && <StickAttributes attributes={attributes} />}

      <div className={styles.actions}>
        {isStockStatusLoading && <Loader data-testid="singleFlavorBlockLoader" />}

        {!isStockStatusLoading && isAvailableInStores && (
          <div className={styles.stockInfo}>
            <StoreDefaultIcon />
            <div className={styles.stockInfoContent}>{__("product.label.availableinstores")}</div>
          </div>
        )}

        {!isStockStatusLoading && !isInStock && !isAvailableInStores && (
          <div className={styles.stockInfo}>
            <div className={styles.warningDot}></div>
            <PackageCrossIcon />
            <div className={styles.stockInfoContent}>{__("productdetails.sticks.outOfStock")}</div>
          </div>
        )}
      </div>
    </div>
  );
};
